import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { formatDate, getProfileImage } from "../../../core/api";
import { deleteUser, getUsers } from "../../../core/services/userService";
import { canManageUsers } from "../../../core/adminAccess";
import MetricCard from "../../../shared/MetricCard";
import AppImage from "../../../shared/AppImage";
import PaginationBar from "../../../shared/PaginationBar";

const AllUser = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({
    totalUsers: 0,
    customerCount: 0,
    adminCount: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    total: 0,
  });
  const [filters, setFilters] = useState({ search: "", isActive: "all" });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchUsers = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          search: filters.search,
          page: String(page),
          limit: String(pagination.limit),
        });

        if (filters.isActive !== "all") {
          params.append("isActive", filters.isActive);
        }

        const res = await getUsers(params.toString());
        if (res.success) {
          setUsers(res.users || []);
          setStats(res.stats || []);
          setPagination({
            page: res.pagination?.page || page,
            limit: res.pagination?.limit || pagination.limit,
            totalPages: res.pagination?.totalPages || 1,
            total: res.pagination?.total ?? res.totalUsers ?? (res.users || []).length,
          });
        }
      } catch (error: any) {
        Swal.fire(
          "Error Loading Users",
          error.message || "Failed to load user list",
          "error",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [filters, pagination.limit],
  );

  useEffect(() => {
    fetchUsers(1);
  }, [fetchUsers]);

  const handleDeleteUser = async (user: any) => {
    const confirm = await Swal.fire({
      title: `Delete User ${user.name}?`,
      text: "This user account and associated cart/wishlist records will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete Account",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await deleteUser(user._id);
      if (res.success) {
        Swal.fire({
          title: "User Deleted",
          text: "User account deleted successfully",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        fetchUsers(pagination.page);
      }
    } catch (error: any) {
      Swal.fire(
        "Delete Failed",
        error.message || "Failed to delete user",
        "error",
      );
    }
  };

  const metrics = [
    {
      label: "Total Users",
      value: stats.totalUsers || users.length,
      icon: "👥",
      tone: "blue",
    },
    {
      label: "Active Users",
      value: stats.activeUsers || 0,
      icon: "🛍️",
      tone: "orange",
    },
    {
      label: "Inactive Users",
      value: stats.inactiveUsers || 0,
      icon: "🛡️",
      tone: "purple",
    },
  ];

  return (
    <>
      {/* Header */}
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">User Management</div>
          <h1 className="page-title">
            All <em>Users</em>
          </h1>
          <div className="page-sub">
            Manage customer accounts and credentials.
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <section className="metrics-grid">
        {metrics.map((metric) => (
          <MetricCard key={metric.label} metric={metric} />
        ))}
      </section>

      {/* Toolbar Filters */}
      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search by name, email, phone..."
          />
        </div>

        <select
          className="filter-select"
          value={filters.isActive}
          onChange={(e) =>
            setFilters({
              ...filters,
              isActive: e.target.value,
            })
          }
        >
          <option value="all">All Users</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>

        <div className="toolbar-right">
          <button
            type="button"
            className="btn-sm btn-outline"
            onClick={() => setFilters({ search: "", isActive: "all" })}
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Users Table */}
      <section className="table-card">
        <div className="table-head">
          <h2>
            Registered Users (
            {pagination.totalPages > 0 ? stats.totalUsers || users.length : 0})
          </h2>
          <p>List of all customers and staff members</p>
        </div>

        <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Joined Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="empty-state">
                  Loading Users...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-state">
                  No Users found matching your search.
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user._id}>
                  <td>
                    <div className="img-name-cell">
                      <AppImage
                        src={getProfileImage(user)}
                        alt={user.name}
                        crossOrigin="anonymous"
                      />
                      <div>
                        <strong>{user.name || "Customer"}</strong>
                        <small className="table-muted">
                          ID: #{user._id?.slice(-6).toUpperCase()}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td>{user.email || "-"}</td>
                  <td>{user.phone || "-"}</td>
                  <td>
                    <span
                      className={`status-badge ${user.isActive ? "success" : "danger"}`}
                    >
                      {user.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>
                    <div className="actions-btns order-actions">
                      <button
                        type="button"
                        onClick={() => navigate(`/users/${user._id}`)}
                        title="View User Details"
                        className="action-button edit"
                      >
                        👁️
                      </button>
                      {canManageUsers() && (
                        <button
                          type="button"
                          onClick={() => navigate(`/users/${user._id}?edit=1`)}
                          title="Edit User"
                          className="action-button edit"
                        >
                          ✏️
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => handleDeleteUser(user)}
                        title="Delete User"
                        className="action-button delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>

        <PaginationBar
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          totalPages={pagination.totalPages}
          itemLabel="users"
          onPageChange={fetchUsers}
          onLimitChange={(limit) =>
            setPagination((current) => ({ ...current, page: 1, limit }))
          }
        />
      </section>
    </>
  );
};

export default AllUser;
